import React from "react";
import { motion } from "framer-motion";
import {
  FaMobileAlt,
  FaAndroid,
  FaApple,
  FaCode,
  FaConnectdevelop,
  FaReact,
  // Icons for sections
  FaRulerCombined,
  FaPencilRuler,
  FaServer,
  FaCheckCircle,
  FaRegLightbulb, // For Creative Ideas
  FaUsers, // For Expert People
  FaRocket, // For Fast Growing Process
} from "react-icons/fa";
import "./AppDevelopment.scss"; 
import ServicesCommon from "../ServicesCommon/ServicesCommon";
import { useNavigate } from "react-router-dom";
import AppDevelopmentHeroImg from "../../../assets/images/appdevelopment.png";
// --- Framer Motion Variants (No change needed here) ---
const sectionFade = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.17, 0.55, 0.55, 1] },
  },
};

const listContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const AppDevelopment = () => {
  const navigate = useNavigate();

  // Data for the Hero Checklist
  const appTechnologies = [
    { icon: FaAndroid, name: "Android App Development" },
    { icon: FaApple, name: "iPhone App Development" },
    { icon: FaConnectdevelop, name: "Flutter App Development" },
    // { icon: FaMobileAlt, name: "Hybrid App Development Services" },
    { icon: FaReact, name: "React Native App Development" },
  ];
  
  // Data for App Development Process
  const processSteps = [
    {
      icon: FaRulerCombined,
      title: "Discovery & Blueprint",
      detail:
        "Defining scope, wireframing user flow, and planning the application architecture.",
    },
    {
      icon: FaPencilRuler,
      title: "UX/UI Design & Prototyping",
      detail:
        "Designing intuitive user interfaces and creating clickable prototypes for validation.",
    },
    {
      icon: FaCode,
      title: "Native & Cross-Platform Coding",
      detail:
        "Writing clean, high-performance code for Android, iOS, or cross-platform deployment.",
    },
    {
      icon: FaCheckCircle,
      title: "Testing & Quality Assurance (QA)",
      detail:
        "Rigorous testing across multiple devices and environments to ensure reliability.",
    },
    {
      icon: FaServer,
      title: "Launch, Store Deployment & Support",
      detail:
        "Managing app store submissions (Apple App Store, Google Play) and providing ongoing maintenance.",
    },
  ];

  // Data for the Core Values
  const coreValues = [
    {
      icon: FaUsers,
      title: "Expert People",
      detail: "Our team consists of certified developers with significant expertise across various industry niches.",
    },
    {
      icon: FaRocket,
      title: "Fast Growing Process",
      detail: "We utilize efficient, modern Agile methodologies to deliver fast, incremental value and business growth.",
    },
    {
      icon: FaRegLightbulb,
      title: "Creative Solutions",
      detail: "We bring innovative ideas and creative strategies to solve complex problems and ensure user engagement.",
    },
  ];


  return (
    <div className="app-dev-page-app">
      {/* ------------------ 1. HERO SECTION (Gold/Amber Theme) ------------------ */}
      <section className="hero-section-app">
        <div className="hero-content-left-app">
          <motion.p
            className="hero-tag-app"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            MOBILE APP DEVELOPMENT
          </motion.p>

          <motion.h1
            className="hero-title-app"
            variants={sectionFade}
            initial="hidden"
            animate="visible"
          >
            Crafting High-Performance Mobile{" "}
            <motion.span
              initial={{ color: "#333" }}
              animate={{ color: "#FEBD47" }} // Gold/Amber accent
              transition={{
                duration: 1.5,
                repeat: Infinity,
                repeatType: "mirror",
              }}
            >
              Applications
            </motion.span>
          </motion.h1>

          <motion.p
            className="hero-subtext-app"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            Econsign Soft’s expert developers can build a mobile app tailored to your business requirement under your budget and in a timeline.
          </motion.p>

          <motion.p
            className="hero-desc-app"
            variants={listItem}
            initial="hidden"
            animate="visible"
          >
            We provide custom mobile app development services, covering everything from UI/UX design and development to full deployment on app stores for all types of businesses.
          </motion.p>
          
          {/* Technologies Checklist */}
          <motion.div
            className="tech-checklist-app"
            variants={listContainer}
            initial="hidden"
            animate="visible"
          >
            {appTechnologies.map((tech, i) => (
              <motion.div className="tech-item-app" key={i} variants={listItem}>
                <FaCheckCircle className="check-icon-app" />
                <span>{tech.name}</span>
              </motion.div>
            ))}
          </motion.div>


          <motion.button
            className="primary-btn-app"
            variants={listItem}
            initial="hidden"
            animate="visible"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 30px rgba(254, 189, 71, 0.4)", // Gold/Amber Shadow
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/contact-us")}
          >
           Contact Us
          </motion.button>
        </div>

        {/* ------------------ Illustration (Image Placeholder) ------------------ */}
        <motion.div
          className="dev-hero-illustration-app"
          initial={{ opacity: 0, x: 50, rotate: 5 }}
          animate={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.5, type: "spring", stiffness: 100 }}
        >
           <img
        src={AppDevelopmentHeroImg} // <--- इम्पोर्ट की गई इमेज
        alt="High Performance Mobile Applications"
        className="responsive-app-img-soft" // <--- CSS के लिए नया क्लासनेम
    />
        </motion.div>
      </section>
      
      {/* ------------------ 2. CORE VALUES / WHY CHOOSE US ------------------ */}
      <section className="core-values-section-app">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Our Core <span className="highlight-value-app">Values</span>
        </motion.h2>

        <motion.div
          className="values-grid-app"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {coreValues.map((value, i) => (
            <motion.div className="value-card-app" key={i} variants={listItem}>
              <value.icon className="value-icon-app" />
              <h3>{value.title}</h3>
              <p>{value.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>
      
      {/* ------------------ 3. APP DEVELOPMENT PROCESS (Timeline) ------------------ */}
      <section className="app-process-section-app">
        <motion.h2
          variants={sectionFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Our <span className="highlight-process-app">Iterative</span> Mobile App
          Development Process
        </motion.h2>

        <motion.div
          className="dev-process-timeline-app"
          variants={listContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {processSteps.map((step, i) => (
            <motion.div className="process-step-app" key={i} variants={listItem}>
              <div className="step-number-wrapper-app">
                <span className="step-number-app">{i + 1}</span>
                <step.icon className="step-icon-app" />
              </div>
              <div className="step-content-app">
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ------------------ 4. COMMON SECTION ------------------ */}
      <ServicesCommon />
    </div>
  );
};

export default AppDevelopment;